/**
 * zod rules for the SubKrewe website / social media fields.
 *
 * Forms hold the links as one object, `links: { website, facebook, … }`
 * (see SharedSubKreweLinkFields), validated by `subkreweLinksSchema`. Empty
 * values pass: every link is optional. Values are checked, not rewritten; the
 * store normalizes them into absolute URLs when it writes them.
 */
import { z } from 'zod'
import type { SubKreweLinkField } from '~/shared/utils/subkreweLinks.js'
import { SUBKREWE_LINK_FIELDS, getSubKreweLinkPlatform, isValidSubKreweLink } from '~/shared/utils/subkreweLinks.js'

/** Every link field, as a form holds it ('' when there is no link). */
export type SubKreweLinkValues = Record<SubKreweLinkField, string>

/** What to say about a value that can't be used, worded for the platform. */
export function subkreweLinkMessage(field: SubKreweLinkField): string {
  return getSubKreweLinkPlatform(field)?.handleBaseUrl
    ? 'Enter a full link (https://…) or an @handle'
    : 'Enter a full link, for example https://mysubkrewe.com'
}

/** One link field: empty, a link, or (on the social platforms) an @handle. */
export function subkreweLinkField(field: SubKreweLinkField) {
  return z.string().refine(value => isValidSubKreweLink(field, value), subkreweLinkMessage(field))
}

/** All link fields, keyed like the SubKrewe document. */
export const subkreweLinksSchema = z.object(
  Object.fromEntries(SUBKREWE_LINK_FIELDS.map(field => [field, subkreweLinkField(field)])) as Record<SubKreweLinkField, z.ZodString>,
)

/** The link values of a SubKrewe (or defaults) for a form: '' where nothing is stored. */
export function subkreweLinkValues(source?: Partial<Record<SubKreweLinkField, unknown>> | null): SubKreweLinkValues {
  return Object.fromEntries(SUBKREWE_LINK_FIELDS.map((field) => {
    const value = source?.[field]
    return [field, typeof value === 'string' ? value : '']
  })) as SubKreweLinkValues
}
